import ActiveRecord from "@/models/ActiveRecord";
import Step from "@/models/Step";
import Option from "@/models/Option";
import Button from "@/models/Button";

export default class Survey extends ActiveRecord {
  // entity is a required property for all models.
  static entity = "survey";

  // List of all fields (schema) of the post model. `this.attr()` declares
  // a generic field type with a default value as the first argument.
  static fields() {
    return {
      id: this.uid(() => this.uuidv4()),
      name: this.string("My survey"),
      user_id: this.attr(null),
      font: this.string("Roboto"),
      fontColor: this.string("#2c3e50"),
      fontColor2: this.string("#2c3e50"),
      bgColor: this.string("#FFFFFF"),
      accentColor: this.string("#27AF60"),
      panelColor: this.string("#FFFFFF"),
      borderColor: this.attr(null),
      buttons: this.hasMany(Button, "survey_id"),
      steps: this.hasMany(Step, "survey_id"),
    };
  }

  get fontFamily() {
    if (this.font == "lato") return "'Lato', sans-serif";
    if (this.font == "lora") return "'Lora', serif";
    if (this.font == "merriweather") return "'Merriweather', serif";
    if (this.font == "montserrat") return "'Montserrat', sans-serif";
    if (this.font == "noto_sans") return "'Noto Sans', sans-serif";
    if (this.font == "noto_jp") return "'Noto Sans JP', sans-serif";
    if (this.font == "open_sans") return "'Open Sans', sans-serif";
    if (this.font == "oswald") return "'Oswald', sans-serif";
    if (this.font == "poppins") return "'Poppins', sans-serif";
    if (this.font == "raleway") return "'Raleway', sans-serif";
    if (this.font == "roboto") return "'Roboto', sans-serif";
    if (this.font == "source_sans") return "'Source Sans Pro', sans-serif";
    else return "'Roboto', sans-serif";
  }

  //Return a specific survey from state, normalized into separate survey,step, option models
  static getNormalizedData(id) {
    var surveyModel = this.find(id);
    var survey = [];
    survey[0] = surveyModel;
    var stepModels = Step.query()
      .where("survey_id", id)
      .get();

    var options = [];
    stepModels.forEach(function(step) {
      //get array of options for this step
      let stepOptions = Option.query()
        .where("step_id", step.id)
        .get();

      //merge into single array of all options
      options = [...options, ...stepOptions];
    });

    var data = {};
    data.survey = survey;
    data.step = stepModels;
    data.option = options;

    return data;
  }

  //Generate sample data for the seed() function
  //fk_val = foreign key value (user_id)
  static dummyData(fk_val) {
    let survey_id = this.uuidv4();

    return {
      id: survey_id,
      user_id: fk_val,
      name: "My new survey",
      fontColor: "#2c3e50",
      bgColor: "#FFFFFF",
      accentColor: "#27AF60",
      panelColor: "#FFFFFF",
      steps: Step.seed(survey_id, 2),
    };
  }

  static sampleSurvey(userId=null) {
    return [
      {
        user_id: userId,
        font: "roboto",
        fontColor: "#2c3e50",
        fontColor2: "#2c3e50",
        bgColor: "#FFFFFF",
        accentColor: "#eee666",
        panelColor: "#FFFFFF",
        buttons: [{
          next_text: "Continue",
          skip_text: "Skip",
          position_x: "center", 
          position_y: "bottom", 
          fontColor: "#2c3e50",
          bgColor: "#FFFFFF", 
        }],
        steps: [
          {
            type: "welcome",
            title: "Welcome to MyApp",
            subtitle: "We have a few quick questions for you to help us customize your experience.",
            image: 'undraw/undraw_online_friends_x73e',
            order: 1,
            options: []
          },
          {
            type: "image",
            title: "Click to edit title",
            subtitle: "Click to edit subtitle",
            order: 2,
            options: [
              {
                title: "First option",
                description: "My description",
                image: "undraw/undraw_User_flow_re_bvfx",
                order: 1,
              },
              {
                title: "Second option",
                description: "My longer description",
                image: "undraw/undraw_Work_chat_re_qes4",
                order: 2,
              },
              {
                title: "Third option",
                description: "My even longer description",
                image: "undraw/undraw_Upload_re_pasx",
                order: 3,
              },
            ],
          },
          {
            type: "image",
            title: "Another step",
            subtitle: "Click to edit subtitle",
            order: 3,
            options: [
              {
                title: "First option",
                description: "My description",
                image: "undraw/undraw_Taking_notes_re_bnaf",
                order: 1,
              },
              {
                title: "Second option",
                description: "My longer description",
                image: "undraw/undraw_Newsletter_re_wrob",
                order: 2,
              },
              {
                title: "Third option",
                description: "My even longer description",
                image: "undraw/undraw_Online_messaging_re_qft3",
                order: 3,
              },
            ],
          },
          {
            type: "end",
            title: "Sweet! You are all done.",
            subtitle: "",
            order: 4,
            image: 'icons/heroicons/solid/check-circle',
            options: []
          }
        ],
      },
    ];
  }

  //Clone a full nested survey object
  static clone(orig) {
    let survey = { ...orig }; //Copy full (nested) survey object
    survey.id = this.uuidv4(); //Generate new survey id and replace old id
    survey.name = survey.name + " (copy)";

    //Replace foreign keys for each step with new survey id
    //survey.steps = this.updateForeignKeys(survey.steps, "survey_id", survey.id);

    survey.steps.forEach((step) => {
      //Generate new survey step id and replace old id

      step.id = this.uuidv4(); //Create new step id
      step.survey_id = survey.id; //Replace foreign key with our new survey id

      step.options.forEach( (option) => {
  
        option.id = this.uuidv4(); //Create new option id
        option.step_id = step.id; //Replace foreign key with our new step id
      });

      /*
      //Replace foreign keys for each step with new step id
      step.options = this.updateForeignKeys(step.options, "step_id", step.id);

      //Replace old option ids with new ids
      step.options = this.generatePrimaryKeys(step.options, "id", step.id);
      */

    });

    return survey;
  }

  static state() {
    return {};
  }

  /*
  //Change all order field values to current state (e.g. after user re-order)
  resetOrder() {
    var steps = this.steps;
    var i;

    for (i = 1; i < steps.length; i++) {
      steps[i]["order"] = i + 1;

      var options = steps[i]['options'];
      var j;

      for (j = 1; j < options.length ; j++) {
        options[i]["order"] = j + 1;
      }
    }
  }
  */
}
